import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useSettingQuery } from '@/features/auth'
import { fetchYakurekiWithDelta } from '@/shared/apis/yakurekiSync'
import {
  executeSuperReload,
  shouldExecuteSuperReload,
} from '@/shared/helpers/superReload'
import type { Yakureki } from '@/shared/types/yakurekiCore'

// sidebar 薬歴リストの取得 hook.
//
// 旧実装は `fetchAllYakurekiPages()` を 1 度叩くだけ (= ポーリング不在) で、 他経路 (NSIPS/レセコン)
// で server 側に作成された event が reload するまで左 panel に出なかった。
//
// legacy (`features/patients/.../usePatientListQuery`) と同じく **delta endpoint の 2 秒ポーリング**
// で時間駆動同期する。delta (`/api/yakureki/delta/`) は無キャッシュ・差分のみ・updated_at index で軽量。
//   - 初回 / super-reload / delta 失敗時のみ全件 (list, 10s cache) を踏む。steady-state は delta。
//   - LS (`yakureki_data` / `yakureki_last_updated`) を merge store に使う共有機構を再利用。
//
// query key は `['yakurekiList', ...]` を維持する。 録音ライフサイクル側の
// `invalidateQueries(['yakurekiList'])` (= useRecordingStatus / MeetingBlock) が prefix 一致で
// **即時 refetch** を駆動し続け、 2 秒 tick を待たずに反映される。
//
// window: server が list/delta とも `display_days` で絞るため、 front も同 displayDays で merge する
// (= server 可視集合と一致。 日付ブラウズは非回帰)。
export const useSidebarYakurekiList = () => {
  const queryClient = useQueryClient()
  const { data: setting } = useSettingQuery()
  // 表示日数は ui_config (UserUIConfig) 管理。 既定 7。 取得前は 7 で仮置きし、 設定到着で key が変わり refetch。
  const displayDays = setting?.ui_config?.display_days ?? 7

  const { data, isLoading } = useQuery({
    queryKey: ['yakurekiList', 'sidebar', 'all', displayDays],
    queryFn: async (): Promise<Yakureki[]> => {
      // 1 日 1 回は全件リセット (= LS の歪み是正)。 それ以外は差分同期。
      if (shouldExecuteSuperReload()) {
        return executeSuperReload()
      }
      return fetchYakurekiWithDelta(displayDays)
    },
    staleTime: 0,
    refetchInterval: 2000,
    refetchIntervalInBackground: false,
  })

  // 手動リロード。 LS (`yakureki_data` / `yakureki_last_updated`) を一掃して全件取り直し、
  // `['yakurekiList']` を invalidate して即時反映する (= legacy の「患者リストを更新」相当)。
  // 別 DB への切替や delta 取りこぼし等で list が歪んだときの復旧導線。
  const reload = useCallback(async () => {
    await executeSuperReload()
    await queryClient.invalidateQueries({ queryKey: ['yakurekiList'] })
  }, [queryClient])

  return { data, isLoading, reload }
}
